import React from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import type { DialOpContactWindow } from '../../../types/dialingOpportunity';

const DEFAULT_VISIBLE_STATES = 10;

function formatHour(hour: number): string {
  const period = hour >= 12 ? 'p' : 'a';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}${period}`;
}

export default function ContactWindowHeatmap({
  window,
  baselineWeeks = 12,
}: {
  window: DialOpContactWindow;
  baselineWeeks?: number;
}) {
  const theme = useTheme();
  const { hours, states } = window;
  const [showAll, setShowAll] = React.useState(false);
  const hasMoreStates = states.length > DEFAULT_VISIBLE_STATES;
  // Already sorted most-active-first by the backend -- "top 10" is just the
  // first 10 rows. Ranking/scale (below) still consider every state so
  // colors and bars don't jump around when expanding.
  const visibleStates = showAll ? states : states.slice(0, DEFAULT_VISIBLE_STATES);

  // States arrive sorted by current-period volume (most active first). Which
  // states show up at all is controlled by the global State filter above the
  // page's tabs (default: every state with enough call history) -- there's
  // no separate per-widget selector here anymore, so this always renders
  // whatever the backend already scoped the query to.
  const maxPct = React.useMemo(() => {
    let max = 0;
    for (const s of states) {
      for (const key of Object.keys(s.cells)) {
        max = Math.max(max, s.cells[key].connect_pct);
      }
    }
    return max || 1;
  }, [states]);

  // Peak current-period dial volume across visible cells, used to scale the
  // per-cell effort bar so dialing effort and answer-likelihood read together.
  const maxCurrentDials = React.useMemo(() => {
    let max = 0;
    for (const s of states) {
      for (const key of Object.keys(s.cells)) {
        max = Math.max(max, s.cells[key].current_dials);
      }
    }
    return max || 1;
  }, [states]);

  if (!states.length) {
    return null;
  }

  const cellColor = (pct: number) => {
    const ratio = Math.min(1, pct / maxPct);
    return alpha(theme.palette.success.main, 0.08 + ratio * 0.82);
  };

  const stateColWidth = 52;
  const cellMinWidth = 34;

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        overflow: 'hidden',
      })}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700}>
            Best time to call, by state
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            icon={<HistoryRounded sx={{ fontSize: 15 }} />}
            label={`Last ${baselineWeeks} weeks`}
            sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          How often you reach new leads at each hour of their local time, based on the last{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>{baselineWeeks} weeks</Box>. Darker means you
          reach more people; the star is each state&apos;s best hour. The blue bar under each cell shows
          how much you actually dialed that hour this period — so you can see effort landing away from
          the best times. This is a fixed benchmark — it does{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>not</Box> change when you change the period
          above.
          {hasMoreStates
            ? ` Showing your top ${DEFAULT_VISIBLE_STATES} most-active states by default.`
            : ''}
        </Typography>
      </Box>

      <Box sx={{ p: 2.5, overflowX: 'auto' }}>
        <Box sx={{ minWidth: stateColWidth + hours.length * cellMinWidth }}>
          {/* Header row: hours */}
          <Stack direction="row" sx={{ mb: 0.5 }}>
            <Box sx={{ width: stateColWidth, flexShrink: 0 }} />
            {hours.map((h) => (
              <Box
                key={h}
                sx={{
                  flex: 1,
                  minWidth: cellMinWidth,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {formatHour(h)}
                </Typography>
              </Box>
            ))}
          </Stack>

          {/* One row per state */}
          {visibleStates.map((s) => (
            <Stack key={s.state_cd ?? 'unknown'} direction="row" alignItems="center" sx={{ mb: 0.5 }}>
              <Box sx={{ width: stateColWidth, flexShrink: 0, pr: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {s.state_cd ?? '—'}
                </Typography>
              </Box>
              {hours.map((h) => {
                const cell = s.cells[String(h)];
                const isBest = s.best_hour === h;
                const tooltip = cell
                  ? `${s.state_cd} · ${formatHour(h)}
Reached: ${cell.connect_pct.toFixed(1)}% of calls (last ${baselineWeeks} weeks)
Calls this period: ${cell.current_dials.toLocaleString()}`
                  : `${s.state_cd} · ${formatHour(h)} · not enough history yet`;

                return (
                  <Tooltip
                    key={h}
                    title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: cellMinWidth,
                        height: 30,
                        mx: '1px',
                        borderRadius: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: 'default',
                        bgcolor: cell
                          ? cellColor(cell.connect_pct)
                          : alpha(theme.palette.text.disabled, 0.06),
                        border: isBest
                          ? `2px solid ${theme.palette.warning.main}`
                          : '1px solid transparent',
                      }}
                    >
                      {isBest ? (
                        <StarRounded
                          sx={{ fontSize: 14, color: theme.palette.warning.main }}
                        />
                      ) : null}
                      {/* Dialing-effort bar: width ∝ this period's call volume,
                          so effort and answer-likelihood read in one picture. */}
                      {(cell?.current_dials ?? 0) > 0 ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 1,
                            right: 1,
                            bottom: 1,
                            height: 3,
                            borderRadius: 1,
                            width: `${Math.max(
                              12,
                              Math.round((100 * (cell?.current_dials ?? 0)) / maxCurrentDials),
                            )}%`,
                            bgcolor: theme.palette.primary.main,
                            opacity: 0.9,
                          }}
                        />
                      ) : null}
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          ))}
        </Box>

        {hasMoreStates ? (
          <Button
            size="small"
            onClick={() => setShowAll((v) => !v)}
            startIcon={showAll ? <ExpandLessRounded /> : <ExpandMoreRounded />}
            sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
          >
            {showAll ? `Show top ${DEFAULT_VISIBLE_STATES} only` : `Show all ${states.length} states`}
          </Button>
        ) : null}
      </Box>

      {/* Legend */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Reach fewer
            </Typography>
            <Box sx={{ display: 'flex' }}>
              {[0.1, 0.3, 0.5, 0.7, 0.9].map((r) => (
                <Box
                  key={r}
                  sx={{
                    width: 18,
                    height: 12,
                    bgcolor: alpha(theme.palette.success.main, 0.08 + r * 0.82),
                  }}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Reach more
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <StarRounded sx={{ fontSize: 14, color: theme.palette.warning.main }} />
            <Typography variant="caption" color="text.secondary">
              Best time
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 16,
                height: 3,
                borderRadius: 1,
                bgcolor: theme.palette.primary.main,
                opacity: 0.9,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Your dialing volume this period
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
